import { useMemo, type ReactNode } from 'react';
import type { IChangeAction } from '@models/Forms/IForms';
import { isDocumentFieldKey, type ChangeRuntime, type IChangeFieldBinding } from '@utils/managers/ChangeRuntime';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { ChangeHandlersContext, type IChangeHandlersContext } from './ChangeHandlersContext';
import { useChangeRuntime } from './useChangeHandlers';

const EMPTY_ACTIONS: IChangeAction[] = [];

export interface IChangeHandlersProviderProps {
    children: ReactNode;
    runtime?: ChangeRuntime;
    changeActions?: IChangeAction[];
    /** Цель откладывается: форма строки копит изменения полей документа до применения строки. */
    deferChange?: (binding: IChangeFieldBinding) => boolean;
    onDeferredChange?: (binding: IChangeFieldBinding, value: any, rowData?: any, rowOldData?: any) => void;
}

export const ChangeHandlersProvider = ({
    children,
    runtime,
    changeActions = EMPTY_ACTIONS,
    deferChange,
    onDeferredChange,
}: IChangeHandlersProviderProps) => {
    const fallbackRuntime = useChangeRuntime(runtime ? EMPTY_ACTIONS : changeActions);
    const ownedRuntime = runtime ?? fallbackRuntime;
    // Колбэки читаются через ref: их identity не должна менять value контекста — иначе
    // каждый перерендер пересобирал бы bridge таблиц и перерегистрировал их валидацию.
    const deferChangeRef = useLatest(deferChange);
    const onDeferredChangeRef = useLatest(onDeferredChange);
    const value = useMemo<IChangeHandlersContext>(
        () => ({
            runtime: ownedRuntime,
            runSavedChanges: async (
                binding,
                savedValue,
                rowData,
                rowOldData,
                invocation,
            ) => {
                if (invocation?.isCurrent?.() === false) return false;
                const documentTarget = isDocumentFieldKey(binding.key);
                const resolvedBinding = {
                    ...binding,
                    scopeId: documentTarget ? undefined : binding.scopeId ?? invocation?.scopeId,
                };
                // Обработчик поля документа не получает строку источника: иначе его addRow/updateRow
                // работают в контексте чужой строки (ChangesManager.invokeSavedTarget).
                const targetRow = documentTarget ? undefined : rowData;
                const targetOldRow = documentTarget ? undefined : rowOldData;
                if (deferChangeRef.current?.(resolvedBinding)) {
                    onDeferredChangeRef.current?.(resolvedBinding, savedValue, targetRow, targetOldRow);
                    return true;
                }
                return ownedRuntime.invoke(
                    resolvedBinding,
                    savedValue,
                    invocation ?? { activeTargets: new Set() },
                    targetRow,
                    targetOldRow,
                );
            },
        }),
        [ownedRuntime, deferChangeRef, onDeferredChangeRef],
    );
    return <ChangeHandlersContext.Provider value={value}>{children}</ChangeHandlersContext.Provider>;
};
